import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, imgUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useMediaPlayer } from "../lib/mediaPlayer";
import { Upload, Loader2, Music2, Video, ImageIcon, ArrowRight, LogOut, Pencil, Trash2, X, Check, Play, Pause, KeyRound } from "lucide-react";
import Seo from "../components/Seo";

// Creator dashboard — upload form with three tabs (Audio / Video / Photo) and
// a list of the current creator's own uploads. Requires role=creator OR
// role=admin (backend enforces this via `require_creator_or_admin`).
const KIND_TABS = [
  { key: "audio", label: "AUDIO", Icon: Music2, color: "#FCA311", hint: "MP3 or WAV. Or paste a YouTube URL / ID." },
  { key: "video", label: "VIDEO", Icon: Video, color: "#E63946", hint: "MP4 or paste a YouTube URL / ID." },
  { key: "photo", label: "PHOTO", Icon: ImageIcon, color: "#67E8F9", hint: "JPG / PNG / WEBP for the SyndaFam gallery." },
];

// The audio/video track upload supports both a YouTube URL and a file upload.
const BLANK_TRACK = { title: "", artist: "", youtube_id: "", audio_url: "", media_type: "audio", media_types: ["audio"] };
const BLANK_PHOTO = { image_path: "", caption: "", section: 1 };

// Default section-pill palette — mirrors the admin defaults if the runtime
// config hasn't loaded yet. Colours match the on-page player.
const DEFAULT_PILLS = [
  { key: "audio", label: "AUDIO", color: "#FCA311" },
  { key: "video", label: "VIDEO", color: "#E63946" },
  { key: "instrumental", label: "INSTRUMENTAL", color: "#67E8F9" },
  { key: "podcast", label: "PODCAST", color: "#A78BFA" },
];

export default function Creator() {
  const nav = useNavigate();
  const { user, loading, logout } = useAuth();
  const mp = useMediaPlayer();
  const [tab, setTab] = useState("audio");
  const [trackForm, setTrackForm] = useState(BLANK_TRACK);
  const [photoForm, setPhotoForm] = useState(BLANK_PHOTO);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myUploads, setMyUploads] = useState({ tracks: [], photos: [] });
  // Inline editors for uploaded items — { kind: "track"|"photo", id, form }
  const [editing, setEditing] = useState(null);
  // Lightbox for full-size photo preview.
  const [previewPhoto, setPreviewPhoto] = useState(null);
  // Matched artist profile (if the creator's username matches an artist).
  const [myArtist, setMyArtist] = useState(null);
  const [artistLinks, setArtistLinks] = useState({});
  const [artistLinksSaving, setArtistLinksSaving] = useState(false);
  // Every library track whose `artist` field mentions the matched artist,
  // regardless of who uploaded it. Empty when there's no artist match.
  const [matchedLibrary, setMatchedLibrary] = useState([]);
  // Live pill palette pulled from the admin player config so labels/colours
  // exactly match what the visitor sees in the on-page FamRadioPlayer.
  const [pillOptions, setPillOptions] = useState(DEFAULT_PILLS);
  // Self-service password change — collapsed panel below the header.
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  useEffect(() => {
    const load = () => {
      api.get("/player/config").then(({ data }) => {
        if (Array.isArray(data?.media_types) && data.media_types.length) {
          setPillOptions(data.media_types);
        }
      }).catch(() => { /* silent — fall back to defaults */ });
    };
    load();
    // Stay in lock-step with admin edits — same event + BroadcastChannel
    // the MediaPlayerProvider listens to, so a new pill added on Admin
    // shows up here without a reload.
    const onUpdated = () => load();
    window.addEventListener("syndafam:player-config-updated", onUpdated);
    let bc = null;
    try {
      if (typeof BroadcastChannel !== "undefined") {
        bc = new BroadcastChannel("syndafam:player-config");
        bc.onmessage = () => load();
      }
    } catch (_) { /* noop */ }
    return () => {
      window.removeEventListener("syndafam:player-config-updated", onUpdated);
      if (bc) { try { bc.close(); } catch (_) { /* noop */ } }
    };
  }, []);

  // Route guard: kick back to /signin if the visitor isn't a creator or admin.
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/signin"); return; }
    if (user.role !== "creator" && user.role !== "admin") { nav("/"); return; }
  }, [loading, user, nav]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/creator/my-uploads");
      setMyUploads(data || { tracks: [], photos: [] });
    } catch (e) { /* silent */ }
    // Also refresh the matched artist library so edits/deletes there stay in
    // sync. Silent on 404 / no-match — the endpoint returns { tracks: [] }.
    try {
      const { data } = await api.get("/creator/matched-library");
      setMatchedLibrary(Array.isArray(data?.tracks) ? data.tracks : []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    if (user && (user.role === "creator" || user.role === "admin")) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Fetch the artist record whose name matches this creator (if any) so we
  // can offer a scoped "edit my artist links" panel.
  useEffect(() => {
    if (!user || (user.role !== "creator" && user.role !== "admin")) return;
    api.get("/creator/my-artist")
      .then(({ data }) => {
        setMyArtist(data);
        setArtistLinks({
          instagram_url: data.instagram_url || "",
          twitter_url: data.twitter_url || "",
          youtube_url: data.youtube_url || "",
          spotify_url: data.spotify_url || "",
          apple_music_url: data.apple_music_url || "",
          tiktok_url: data.tiktok_url || "",
          soundcloud_url: data.soundcloud_url || "",
          facebook_url: data.facebook_url || "",
          website_url: data.website_url || "",
          photo_path: data.photo_path || "",
        });
      })
      .catch(() => { setMyArtist(null); });
  }, [user?.user_id]);

  const saveArtistLinks = async (e) => {
    e.preventDefault();
    setArtistLinksSaving(true);
    try {
      const { data } = await api.put("/creator/my-artist/links", artistLinks);
      setMyArtist(data);
      toast.success("Your artist links have been updated");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Save failed");
    } finally { setArtistLinksSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current_password) return toast.error("Enter your current password");
    if (pwForm.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pwForm.new_password !== pwForm.confirm_password) return toast.error("New passwords don't match");
    if (pwForm.new_password === pwForm.current_password) return toast.error("New password must be different");
    setPwSaving(true);
    try {
      await api.put("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated. Use the new one next time you sign in.");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      setPwOpen(false);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Password change failed");
    } finally { setPwSaving(false); }
  };

  // Upload helper — hits the shared /admin/upload endpoint (now permits
  // creators too) and hands the storage_path back to the caller.
  const uploadFile = async (file, kind, onDone) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitTrack = async (e) => {
    e.preventDefault();
    if (!trackForm.title) return toast.error("Give the track a title");
    if (!trackForm.youtube_id && !trackForm.audio_url) return toast.error("Attach a file or paste a YouTube URL");
    if (!trackForm.media_types || trackForm.media_types.length === 0) return toast.error("Pick at least one section pill");
    setSaving(true);
    try {
      // The top tab determines the PRIMARY pill (audio/video) and drives which
      // file-input accept string we use — but creators can additionally tag
      // their track with extra pills (e.g. put an instrumental beat under
      // BOTH the AUDIO and INSTRUMENTAL sections).
      const primary = trackForm.media_types.includes(tab) ? tab : trackForm.media_types[0];
      await api.post("/creator/upload/track", {
        ...trackForm,
        media_type: primary,
        media_types: trackForm.media_types,
      });
      toast.success(`Track submitted to: ${trackForm.media_types.map((k) => k.toUpperCase()).join(", ")}`);
      setTrackForm({ ...BLANK_TRACK, media_type: tab, media_types: [tab] });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSaving(false); }
  };

  const submitPhoto = async (e) => {
    e.preventDefault();
    if (!photoForm.image_path) return toast.error("Upload a photo first");
    setSaving(true);
    try {
      await api.post("/creator/upload/gallery-photo", photoForm);
      toast.success("Photo submitted for the gallery");
      setPhotoForm(BLANK_PHOTO);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSaving(false); }
  };

  // Manage-your-uploads helpers (delete + inline edit).
  const startEditTrack = (t) => setEditing({
    kind: "track",
    id: t.id,
    form: {
      title: t.title || "",
      artist: t.artist || "",
      media_type: t.media_type || "audio",
      media_types: (Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"]),
      youtube_id: t.youtube_id || "",
      audio_url: t.audio_url || "",
    },
  });
  const startEditPhoto = (p) => setEditing({
    kind: "photo",
    id: p.id,
    form: { caption: p.caption || "", section: p.section || 1 },
  });
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const endpoint = editing.kind === "track"
        ? `/creator/uploads/track/${editing.id}`
        : `/creator/uploads/photo/${editing.id}`;
      await api.put(endpoint, editing.form);
      toast.success("Updated");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };
  const deleteTrack = async (t) => {
    if (!window.confirm(`Delete "${t.title || "this track"}"? This removes it from the library and cannot be undone.`)) return;
    try {
      await api.delete(`/creator/uploads/track/${t.id}`);
      toast.success("Track deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const deletePhoto = async (p) => {
    if (!window.confirm(`Delete "${p.caption || "this photo"}"? This removes it from the gallery.`)) return;
    try {
      await api.delete(`/creator/uploads/photo/${p.id}`);
      toast.success("Photo deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Play a specific track in the shared media player. Toggles pause/play if
  // the track is already the current one — otherwise switches to the correct
  // section pill and starts fresh so the visitor hears the right source.
  const playMyTrack = (t) => {
    const isCurrent = mp.currentTrack?.id === t.id;
    if (isCurrent) {
      mp.togglePlay?.();
      return;
    }
    // Pick a media type that this track supports and that the player has
    // configured, then switch context to that section so it can find the
    // track in the visible playlist.
    const types = Array.isArray(t.media_types) && t.media_types.length
      ? t.media_types
      : [t.media_type || "audio"];
    if (types[0] && mp.setMediaType) mp.setMediaType(types[0]);
    mp.playTrack?.(t.id);
    // Ensure it's audible.
    if (mp.muted && mp.setMuted) mp.setMuted(false);
    mp.setShowMini?.(true);
  };

  // Shared renderer for a single track row so both "YOUR RECENT SUBMISSIONS"
  // and the matched-artist "LIBRARY" list stay identical (edit/play/delete
  // all wired the same). Passing the `context` string keeps the data-testid
  // scoped per list so playwright can tell them apart.
  const renderTrackRow = (t, context) => {
    const isEditing = editing?.kind === "track" && editing.id === t.id;
    if (isEditing) {
      return (
        <div key={`${context}-${t.id}`} className="rounded-2xl border border-[#FCA311]/60 bg-white/[0.04] backdrop-blur-xl p-5 space-y-3 shadow-[0_0_30px_-10px_rgba(252,163,17,0.7)]" data-testid={`creator-track-edit-${t.id}`}>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">// EDITING TRACK</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Title" value={editing.form.title} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, title: e.target.value } })} className="bg-white/[0.03] border border-white/10 focus:border-[#FCA311] outline-none rounded-2xl px-4 py-2.5 font-mono text-sm transition-colors" data-testid="creator-edit-title" />
            <input placeholder="Artist" value={editing.form.artist} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, artist: e.target.value } })} className="bg-white/[0.03] border border-white/10 focus:border-[#FCA311] outline-none rounded-2xl px-4 py-2.5 font-mono text-sm transition-colors" data-testid="creator-edit-artist" />
          </div>
          <select value={editing.form.media_type} onChange={(e) => {
            const newPrimary = e.target.value;
            setEditing({ ...editing, form: { ...editing.form, media_type: newPrimary, media_types: Array.from(new Set([...(editing.form.media_types || []), newPrimary])) } });
          }} className="w-full bg-white/[0.03] border border-white/10 focus:border-[#FCA311] outline-none rounded-2xl px-4 py-2.5 font-mono text-sm transition-colors" data-testid="creator-edit-mediatype">
            {pillOptions.map((mt) => (
              <option key={mt.key} value={mt.key}>{(mt.label || mt.key).toUpperCase()}</option>
            ))}
          </select>
          <div className="space-y-1" data-testid="creator-edit-pills">
            <div className="font-mono text-[10px] tracking-widest text-white/50">// SECTION PILLS</div>
            <div className="flex flex-wrap gap-2">
              {pillOptions.map((mt) => {
                const active = (editing.form.media_types || []).includes(mt.key);
                return (
                  <button
                    key={mt.key}
                    type="button"
                    onClick={() => {
                      const s = new Set(editing.form.media_types || []);
                      if (s.has(mt.key)) s.delete(mt.key); else s.add(mt.key);
                      const arr = Array.from(s);
                      const safe = arr.length ? arr : [editing.form.media_type || "audio"];
                      setEditing({ ...editing, form: { ...editing.form, media_types: safe, media_type: safe.includes(editing.form.media_type) ? editing.form.media_type : safe[0] } });
                    }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-mono text-[10px] tracking-[0.3em] transition-all"
                    style={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: active ? (mt.color || "#FCA311") : "rgba(255,255,255,0.15)",
                      color: active ? "#000" : "rgba(255,255,255,0.7)",
                      background: active ? (mt.color || "#FCA311") : "rgba(255,255,255,0.03)",
                      boxShadow: active ? `0 0 20px -4px ${mt.color || "#FCA311"}88` : undefined,
                    }}
                    data-testid={`creator-edit-pill-${mt.key}`}
                  >
                    {active ? <Check className="w-3 h-3" /> : null}{(mt.label || mt.key).toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
          <input placeholder="YouTube URL/ID" value={editing.form.youtube_id} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, youtube_id: e.target.value, audio_url: e.target.value ? "" : editing.form.audio_url } })} className="w-full bg-white/[0.03] border border-white/10 focus:border-[#FCA311] outline-none rounded-2xl px-4 py-2.5 font-mono text-sm transition-colors" data-testid="creator-edit-yt" />
          <input placeholder="Or direct file URL" value={editing.form.audio_url} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, audio_url: e.target.value, youtube_id: e.target.value ? "" : editing.form.youtube_id } })} className="w-full bg-white/[0.03] border border-white/10 focus:border-[#FCA311] outline-none rounded-2xl px-4 py-2.5 font-mono text-sm transition-colors" data-testid="creator-edit-url" />
          <div className="flex gap-2 justify-end">
            <button onClick={cancelEdit} className="font-mono text-[11px] tracking-[0.25em] px-4 py-2 rounded-full border border-white/15 hover:border-white/40 inline-flex items-center gap-1 transition-colors" data-testid="creator-edit-cancel"><X className="w-3 h-3" /> CANCEL</button>
            <button onClick={saveEdit} disabled={saving} className="font-mono text-[11px] tracking-[0.25em] px-4 py-2 rounded-full bg-[#FCA311] hover:bg-white text-black disabled:opacity-50 inline-flex items-center gap-1 transition-colors" data-testid="creator-edit-save"><Check className="w-3 h-3" /> {saving ? "SAVING…" : "SAVE"}</button>
          </div>
        </div>
      );
    }
    const provenance = t.uploaded_by === user.user_id
      ? { label: "MINE", color: "#FCA311" }
      : { label: `BY ${(t.uploaded_by_username || "TEAM").toUpperCase()}`, color: "#67E8F9" };
    return (
      <div key={`${context}-${t.id}`} className="grid grid-cols-[auto_auto_1fr_auto] gap-3 items-center rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 px-4 py-3 transition-colors" data-testid={`creator-track-row-${t.id}`}>
        <button
          onClick={() => playMyTrack(t)}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${mp.currentTrack?.id === t.id && mp.playing ? "bg-[#E63946] text-white shadow-[0_0_24px_-4px_rgba(230,57,70,0.9)]" : "bg-white/5 text-white/80 hover:bg-[#E63946] hover:text-white hover:shadow-[0_0_20px_-4px_rgba(230,57,70,0.9)]"}`}
          title={mp.currentTrack?.id === t.id && mp.playing ? "Pause" : "Play in the mini player"}
          aria-label={mp.currentTrack?.id === t.id && mp.playing ? "Pause" : "Play"}
          data-testid={`creator-track-play-${t.id}`}
        >
          {mp.currentTrack?.id === t.id && mp.playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <div className="flex flex-col gap-1 items-start">
          {(Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"]).map((k) => {
            const meta = pillOptions.find((p) => p.key === k) || { color: "#FCA311", label: k };
            return (
              <span key={k} className="font-mono text-[9px] tracking-[0.2em] px-2 py-0.5 rounded-full border" style={{ borderColor: meta.color, color: meta.color }}>
                {(meta.label || k).toUpperCase()}
              </span>
            );
          })}
          <span className="font-mono text-[8px] tracking-[0.2em] px-1.5 py-0.5 rounded-full border" style={{ borderColor: provenance.color, color: provenance.color }}>
            {provenance.label}
          </span>
        </div>
        <div className="min-w-0">
          <div className="font-display text-base truncate">{t.title || "(untitled)"}</div>
          <div className="font-mono text-[10px] text-white/40 truncate">{t.artist || "—"} · {t.youtube_id ? `YT:${t.youtube_id}` : (t.audio_url ? "file" : "no source")}</div>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-mono text-[10px] text-white/30 mr-2 hidden sm:inline">{new Date(t.created_at || Date.now()).toLocaleDateString()}</span>
          <button onClick={() => startEditTrack(t)} className="p-2 rounded-full text-white/50 hover:text-[#FCA311] hover:bg-white/5 transition-colors" title="Edit" data-testid={`creator-track-edit-btn-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => deleteTrack(t)} className="p-2 rounded-full text-white/50 hover:text-[#E63946] hover:bg-white/5 transition-colors" title="Delete" data-testid={`creator-track-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    );
  };

  if (loading || !user) return null;

  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-white pt-24 pb-24 px-5 sm:px-8 overflow-hidden" data-testid="creator-dashboard">
      <Seo title="Creator Dashboard · SyndaFam" description="Upload tracks, videos, and photos to the SyndaFam library." />
      {/* Living aurora + drifting orbs to match the rest of the site's
          futuristic dark aesthetic (Artists, Home). Pointer-events off
          so they never eat clicks. */}
      <div className="fixed inset-0 -z-10 bg-aurora pointer-events-none opacity-60" aria-hidden="true" />
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="orb orb-red orb-live"   style={{ width: 620, height: 620, top: "-180px", left: "-200px", opacity: 0.35 }} />
        <div className="orb orb-amber orb-live" style={{ width: 520, height: 520, bottom: "-160px", right: "-140px", opacity: 0.28, animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live"  style={{ width: 380, height: 380, top: "38%", right: "8%", opacity: 0.15, animationDelay: "-3s" }} />
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* Hero card — greeting + shortcut buttons live in one glass slab
            so the top of the dashboard feels like a proper cockpit rather
            than a raw form page. */}
        <div className="glass-card-strong rounded-3xl p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-start justify-between gap-6" data-testid="creator-header-card">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-3">// CREATOR · DASHBOARD</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[0.95]">HELLO, <span className="text-[#FCA311]" style={{ textShadow: "0 0 24px rgba(252,163,17,0.6)" }}>{(user.name || user.username || "").toUpperCase()}</span>.</h1>
            <p className="font-mono text-[11px] leading-relaxed text-white/55 mt-4 max-w-xl">
              Everything you upload lands in the SyndaFam library for review — pick a media type, drop the file (or paste a YouTube link) and hit SUBMIT.
            </p>
          </div>
          <div className="flex-shrink-0 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPwOpen((v) => !v)}
              className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] px-4 py-2.5 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur hover:border-[#FCA311] hover:text-[#FCA311] hover:shadow-[0_0_20px_-4px_rgba(252,163,17,0.6)] transition-all"
              data-testid="creator-change-password-toggle"
              aria-expanded={pwOpen}
            >
              <KeyRound className="w-3.5 h-3.5" /> {pwOpen ? "CANCEL" : "CHANGE PASSWORD"}
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] px-4 py-2.5 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur hover:border-[#E63946] hover:text-[#E63946] hover:shadow-[0_0_20px_-4px_rgba(230,57,70,0.6)] transition-all"
              data-testid="creator-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> LOG OUT
            </button>
          </div>
        </div>

        {/* Self-service password change — creators (and admins signed in via
            /signin) can rotate their own password without asking an admin.
            Backend verifies the current password, hashes the new one with
            bcrypt, then revokes every session and mints a fresh one for
            this tab so the user stays signed in here but is booted from
            every other device. */}
        {pwOpen && (
          <form onSubmit={changePassword} className="glass-card-strong rounded-3xl p-6 mb-8 space-y-4 border border-[#FCA311]/40 shadow-[0_0_40px_-16px_rgba(252,163,17,0.6)]" data-testid="creator-change-password-panel">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">// CHANGE PASSWORD</div>
              <span className="font-mono text-[9px] tracking-widest text-white/40">Signs out every other device on save.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="password"
                autoComplete="current-password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                placeholder="CURRENT PASSWORD"
                className="bg-white/[0.03] border border-white/10 focus:border-[#FCA311] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3 font-mono text-xs tracking-widest transition-colors"
                data-testid="creator-pw-current"
              />
              <input
                type="password"
                autoComplete="new-password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                placeholder="NEW PASSWORD (MIN 8)"
                className="bg-white/[0.03] border border-white/10 focus:border-[#FCA311] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3 font-mono text-xs tracking-widest transition-colors"
                data-testid="creator-pw-new"
              />
              <input
                type="password"
                autoComplete="new-password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                placeholder="CONFIRM NEW PASSWORD"
                className="bg-white/[0.03] border border-white/10 focus:border-[#FCA311] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3 font-mono text-xs tracking-widest transition-colors"
                data-testid="creator-pw-confirm"
              />
            </div>
            <button
              type="submit"
              disabled={pwSaving}
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] bg-[#FCA311] hover:bg-white text-black px-6 py-3 rounded-full transition-all hover:shadow-[0_0_28px_-6px_rgba(252,163,17,0.9)] hover:-translate-y-px disabled:opacity-50"
              data-testid="creator-pw-save"
            >
              {pwSaving ? "SAVING…" : "SAVE NEW PASSWORD"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* Matched artist profile — creators whose username exactly matches
            an artist name get scoped access to update ONLY the links on
            their own artist row. Everything else (bio, photo, name, role)
            stays admin-only. */}
        {myArtist && (
          <form onSubmit={saveArtistLinks} className="glass-card-strong rounded-3xl p-6 sm:p-7 mb-8 space-y-5 border border-[#67E8F9]/30 shadow-[0_0_40px_-16px_rgba(103,232,249,0.5)]" data-testid="creator-my-artist-panel">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#67E8F9]">// LINKED ARTIST PROFILE</div>
              <div className="font-display text-2xl">{myArtist.name}</div>
              <span className="font-mono text-[9px] tracking-widest px-3 py-1 rounded-full bg-[#67E8F9] text-black shadow-[0_0_16px_-2px_rgba(103,232,249,0.9)]">MATCHED</span>
            </div>
            <p className="font-mono text-[11px] leading-relaxed text-white/55">
              Update your profile photo and social + streaming links. All other fields (bio, role, name) stay admin-controlled.
            </p>

            {/* Profile photo — creators can swap their own artist portrait.
                Upload lands via the shared /admin/upload endpoint. */}
            <div className="flex items-center gap-5 rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid="creator-artist-photo-section">
              <div className="w-20 h-20 rounded-2xl border border-white/15 bg-black overflow-hidden flex-shrink-0 shadow-[0_0_24px_-8px_rgba(103,232,249,0.6)]">
                {artistLinks.photo_path ? (
                  <img src={imgUrl(artistLinks.photo_path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-mono text-[9px] text-white/30">NO PHOTO</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] tracking-[0.25em] text-white/60 mb-1">PROFILE PHOTO</div>
                <div className="font-mono text-[10px] text-white/40 truncate mb-3">{artistLinks.photo_path || "(no photo uploaded yet)"}</div>
                <div className="flex flex-wrap gap-2">
                  <label className="font-mono text-[10px] tracking-[0.25em] px-4 py-2 rounded-full border border-white/15 bg-white/[0.03] text-white/80 hover:border-[#67E8F9] hover:text-[#67E8F9] hover:shadow-[0_0_20px_-4px_rgba(103,232,249,0.7)] cursor-pointer inline-flex items-center gap-2 transition-all">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} UPLOAD NEW
                    <input type="file" accept="image/*" className="hidden" data-testid="creator-artist-photo-upload" onChange={(e) => {
                      if (e.target.files?.[0]) {
                        uploadFile(e.target.files[0], "image", (d) => setArtistLinks((cur) => ({ ...cur, photo_path: d.storage_path })));
                        e.target.value = "";
                      }
                    }} />
                  </label>
                  {artistLinks.photo_path && (
                    <button
                      type="button"
                      onClick={() => setArtistLinks((cur) => ({ ...cur, photo_path: "" }))}
                      className="font-mono text-[10px] tracking-[0.25em] px-4 py-2 rounded-full border border-white/15 text-white/60 hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                      data-testid="creator-artist-photo-clear"
                    >
                      REMOVE
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["instagram_url", "INSTAGRAM URL"],
                ["twitter_url", "X / TWITTER URL"],
                ["youtube_url", "YOUTUBE URL"],
                ["spotify_url", "SPOTIFY URL"],
                ["apple_music_url", "APPLE MUSIC URL"],
                ["tiktok_url", "TIKTOK URL"],
                ["soundcloud_url", "SOUNDCLOUD URL"],
                ["facebook_url", "FACEBOOK URL"],
                ["website_url", "WEBSITE URL"],
              ].map(([key, label]) => (
                <input
                  key={key}
                  value={artistLinks[key] || ""}
                  onChange={(e) => setArtistLinks({ ...artistLinks, [key]: e.target.value })}
                  placeholder={label}
                  className="bg-white/[0.03] border border-white/10 focus:border-[#67E8F9] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3 font-mono text-xs transition-colors"
                  data-testid={`creator-artist-link-${key}`}
                />
              ))}
            </div>
            <button type="submit" disabled={artistLinksSaving} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] bg-[#67E8F9] hover:bg-white text-black px-6 py-3 rounded-full transition-all hover:shadow-[0_0_28px_-6px_rgba(103,232,249,0.9)] hover:-translate-y-px disabled:opacity-50" data-testid="creator-artist-links-save">
              {artistLinksSaving ? "SAVING…" : "SAVE ARTIST PROFILE"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* Matched artist library — every track in the SyndaFam library
            whose `artist` field mentions this creator's matched artist,
            regardless of who originally uploaded it. Backend permission
            check for edit/delete uses the same case-insensitive alnum
            contains match so the row-level Pencil / Trash actions Just
            Work here even for admin-uploaded content. */}
        {myArtist && matchedLibrary.length > 0 && (
          <div className="glass-card-strong rounded-3xl p-6 mb-8" data-testid="creator-matched-library">
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#67E8F9]">// {myArtist.name.toUpperCase()} — LIBRARY ({matchedLibrary.length})</div>
              <span className="font-mono text-[9px] tracking-widest text-white/40">Every track featuring you — edit, delete or preview them all in one place.</span>
            </div>
            <div className="space-y-2">
              {matchedLibrary.map((t) => renderTrackRow(t, "library"))}
            </div>
          </div>
        )}

        {/* Segmented control — matches the glass tab bar on the FamRadio
            player so the whole app speaks one visual language. */}
        <div className="mb-6 flex" data-testid="creator-tabs-wrap">
          <div className="inline-flex flex-wrap items-center gap-1 p-1.5 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_10px_40px_-12px_rgba(0,0,0,0.6)]">
            {KIND_TABS.map((k) => {
              const active = tab === k.key;
              const color = k.key === "photo" ? "#67E8F9" : k.key === "video" ? "#E63946" : "#FCA311";
              return (
                <button
                  key={k.key}
                  onClick={() => {
                    setTab(k.key);
                    if (k.key === "audio" || k.key === "video") {
                      setTrackForm((cur) => ({ ...cur, media_type: k.key, media_types: [k.key] }));
                    }
                  }}
                  className={`relative inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] px-5 py-2.5 rounded-full transition-all duration-300 ease-out ${active ? "text-white" : "text-white/50 hover:text-white/90 hover:bg-white/[0.04]"}`}
                  style={active ? {
                    background: `linear-gradient(180deg, ${color}26, ${color}0d)`,
                    boxShadow: `0 0 22px -6px ${color}, inset 0 0 0 1px ${color}66, inset 0 1px 0 rgba(255,255,255,0.18)`,
                  } : undefined}
                  data-testid={`creator-tab-${k.key}`}
                  aria-pressed={active}
                >
                  {active && (
                    <span aria-hidden className="absolute inset-0 rounded-full pointer-events-none" style={{ background: `radial-gradient(70% 120% at 50% 50%, ${color}33, transparent 70%)` }} />
                  )}
                  <span className="relative flex items-center gap-2" style={active ? { color, textShadow: `0 0 12px ${color}77` } : undefined}>
                    <k.Icon className="w-4 h-4" /> {k.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* AUDIO / VIDEO form */}
        {(tab === "audio" || tab === "video") && (
          <form onSubmit={submitTrack} className="glass-card-strong rounded-3xl p-6 sm:p-8 space-y-5" data-testid="creator-track-form">
            <div className="font-mono text-[10px] tracking-[0.3em] text-white/50">// {tab.toUpperCase()} SUBMISSION</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input value={trackForm.title} onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })} placeholder="Track title" className="bg-white/[0.03] border border-white/10 focus:border-[#E63946] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3.5 font-mono text-sm transition-colors" data-testid="creator-track-title" />
              <input value={trackForm.artist} onChange={(e) => setTrackForm({ ...trackForm, artist: e.target.value })} placeholder="Artist(s) — comma / feat. / &" className="bg-white/[0.03] border border-white/10 focus:border-[#E63946] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3.5 font-mono text-sm transition-colors" data-testid="creator-track-artist" />
            </div>
            <input value={trackForm.youtube_id} onChange={(e) => setTrackForm({ ...trackForm, youtube_id: e.target.value, audio_url: "" })} placeholder="YouTube URL or ID (leave empty if you're uploading a file below)" className="w-full bg-white/[0.03] border border-white/10 focus:border-[#E63946] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3.5 font-mono text-sm transition-colors" data-testid="creator-track-yt" />
            <div className="flex flex-wrap items-center gap-3">
              <input value={trackForm.audio_url} onChange={(e) => setTrackForm({ ...trackForm, audio_url: e.target.value, youtube_id: "" })} placeholder="…or paste a direct file URL" className="flex-1 min-w-[240px] bg-white/[0.03] border border-white/10 focus:border-[#E63946] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3.5 font-mono text-sm transition-colors" data-testid="creator-track-url" />
              <label className="font-mono text-[11px] tracking-[0.25em] px-4 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white/70 hover:border-[#FCA311] hover:text-[#FCA311] hover:shadow-[0_0_20px_-4px_rgba(252,163,17,0.7)] cursor-pointer inline-flex items-center gap-2 transition-all">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} UPLOAD FILE
                <input type="file" accept={tab === "video" ? "video/*" : "audio/*"} className="hidden" data-testid="creator-track-upload" onChange={(e) => {
                  if (e.target.files?.[0]) {
                    uploadFile(e.target.files[0], tab === "video" ? "video" : "audio", (d) => setTrackForm((cur) => ({
                      ...cur,
                      audio_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.storage_path}`,
                      youtube_id: "",
                    })));
                    e.target.value = "";
                  }
                }} />
              </label>
            </div>
            <p className="font-mono text-[10px] text-white/40">{KIND_TABS.find((k) => k.key === tab).hint}</p>

            <div className="pt-2 space-y-2" data-testid="creator-track-pills">
              <div className="font-mono text-[10px] tracking-widest text-white/50">// SHOW IN SECTIONS · CHOOSE ONE OR MORE</div>
              <div className="flex flex-wrap gap-2">
                {pillOptions.map((mt) => {
                  const active = (trackForm.media_types || []).includes(mt.key);
                  return (
                    <button
                      key={mt.key}
                      type="button"
                      onClick={() => setTrackForm((cur) => {
                        const cur_set = new Set(cur.media_types || []);
                        if (cur_set.has(mt.key)) cur_set.delete(mt.key); else cur_set.add(mt.key);
                        const arr = Array.from(cur_set);
                        const safe = arr.length ? arr : [tab];
                        return { ...cur, media_types: safe, media_type: safe.includes(tab) ? tab : safe[0] };
                      })}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-mono text-[10px] tracking-[0.3em] transition-all"
                      style={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: active ? (mt.color || "#FCA311") : "rgba(255,255,255,0.15)",
                        color: active ? "#000" : "rgba(255,255,255,0.7)",
                        background: active ? (mt.color || "#FCA311") : "rgba(255,255,255,0.03)",
                        boxShadow: active ? `0 0 20px -4px ${mt.color || "#FCA311"}88` : undefined,
                      }}
                      data-testid={`creator-pill-${mt.key}`}
                      aria-pressed={active}
                    >
                      {active ? <Check className="w-3 h-3" /> : null}{(mt.label || mt.key).toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 font-mono text-[12px] tracking-[0.3em] bg-[#E63946] hover:bg-[#C92A36] disabled:opacity-50 text-white px-7 py-3.5 rounded-full transition-all hover:shadow-[0_0_28px_-6px_rgba(230,57,70,0.9)] hover:-translate-y-px" data-testid="creator-track-submit">
              {saving ? "SUBMITTING…" : "SUBMIT TO LIBRARY"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* PHOTO form */}
        {tab === "photo" && (
          <form onSubmit={submitPhoto} className="glass-card-strong rounded-3xl p-6 sm:p-8 space-y-5" data-testid="creator-photo-form">
            <div className="font-mono text-[10px] tracking-[0.3em] text-white/50">// PHOTO SUBMISSION</div>
            <input value={photoForm.caption} onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })} placeholder="Caption (optional)" className="w-full bg-white/[0.03] border border-white/10 focus:border-[#67E8F9] focus:bg-white/[0.05] outline-none rounded-2xl px-4 py-3.5 font-mono text-sm transition-colors" data-testid="creator-photo-caption" />
            <div className="flex flex-wrap items-center gap-3">
              <input value={photoForm.image_path} readOnly placeholder="Uploaded file path will appear here" className="flex-1 min-w-[240px] bg-white/[0.03] border border-white/10 outline-none rounded-2xl px-4 py-3.5 font-mono text-xs text-white/40" data-testid="creator-photo-path" />
              <label className="font-mono text-[11px] tracking-[0.25em] px-4 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white/70 hover:border-[#67E8F9] hover:text-[#67E8F9] hover:shadow-[0_0_20px_-4px_rgba(103,232,249,0.7)] cursor-pointer inline-flex items-center gap-2 transition-all">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} UPLOAD PHOTO
                <input type="file" accept="image/*" className="hidden" data-testid="creator-photo-upload" onChange={(e) => {
                  if (e.target.files?.[0]) {
                    uploadFile(e.target.files[0], "image", (d) => setPhotoForm((cur) => ({ ...cur, image_path: d.storage_path })));
                    e.target.value = "";
                  }
                }} />
              </label>
            </div>
            {photoForm.image_path && (
              <img src={imgUrl(photoForm.image_path)} alt="preview" className="max-h-56 rounded-2xl border border-white/10 object-cover shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]" />
            )}
            <button type="submit" disabled={saving || !photoForm.image_path} className="inline-flex items-center gap-2 font-mono text-[12px] tracking-[0.3em] bg-[#67E8F9] hover:bg-white disabled:opacity-50 text-black px-7 py-3.5 rounded-full transition-all hover:shadow-[0_0_28px_-6px_rgba(103,232,249,0.9)] hover:-translate-y-px" data-testid="creator-photo-submit">
              {saving ? "SUBMITTING…" : "SUBMIT TO GALLERY"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* My uploads */}
        <div className="mt-12 glass-card-strong rounded-3xl p-6 sm:p-8">
          <div className="font-mono text-[10px] tracking-[0.3em] text-white/50 mb-4">// YOUR RECENT SUBMISSIONS — TAP THE PENCIL TO EDIT, TRASH TO DELETE</div>
          {(myUploads.tracks.length === 0 && myUploads.photos.length === 0) ? (
            <div className="font-mono text-xs text-white/40 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center">Nothing submitted yet — pick a tab and drop your first upload.</div>
          ) : (
            <div className="space-y-2">
              {myUploads.tracks.map((t) => renderTrackRow(t, "mine"))}
              {myUploads.photos.map((p) => {
                const isEditing = editing?.kind === "photo" && editing.id === p.id;
                if (isEditing) {
                  return (
                    <div key={p.id} className="rounded-2xl border border-[#FCA311]/60 bg-white/[0.04] backdrop-blur-xl p-5 space-y-3 shadow-[0_0_30px_-10px_rgba(252,163,17,0.7)]" data-testid={`creator-photo-edit-${p.id}`}>
                      <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">// EDITING PHOTO</div>
                      <img src={imgUrl(p.image_path)} alt="" className="max-h-40 rounded-xl border border-white/10 object-cover" />
                      <input placeholder="Caption" value={editing.form.caption} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, caption: e.target.value } })} className="w-full bg-white/[0.03] border border-white/10 focus:border-[#FCA311] outline-none rounded-2xl px-4 py-2.5 font-mono text-sm transition-colors" data-testid="creator-photo-edit-caption" />
                      <select value={editing.form.section} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, section: parseInt(e.target.value) || 1 } })} className="w-full bg-white/[0.03] border border-white/10 focus:border-[#FCA311] outline-none rounded-2xl px-4 py-2.5 font-mono text-sm transition-colors" data-testid="creator-photo-edit-section">
                        <option value={1}>SECTION 1</option>
                        <option value={2}>SECTION 2</option>
                        <option value={3}>SECTION 3</option>
                        <option value={4}>SECTION 4</option>
                        <option value={5}>SECTION 5 — CREATOR UPLOADS</option>
                      </select>
                      <div className="flex gap-2 justify-end">
                        <button onClick={cancelEdit} className="font-mono text-[11px] tracking-[0.25em] px-4 py-2 rounded-full border border-white/15 hover:border-white/40 inline-flex items-center gap-1 transition-colors"><X className="w-3 h-3" /> CANCEL</button>
                        <button onClick={saveEdit} disabled={saving} className="font-mono text-[11px] tracking-[0.25em] px-4 py-2 rounded-full bg-[#FCA311] hover:bg-white text-black disabled:opacity-50 inline-flex items-center gap-1 transition-colors" data-testid="creator-photo-edit-save"><Check className="w-3 h-3" /> {saving ? "SAVING…" : "SAVE"}</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={p.id} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 px-4 py-3 transition-colors" data-testid={`creator-photo-row-${p.id}`}>
                    <span className="font-mono text-[9px] tracking-[0.3em] text-[#67E8F9] whitespace-nowrap">PHOTO · S{p.section || 1}</span>
                    <div className="min-w-0 flex items-center gap-3">
                      <button
                        onClick={() => setPreviewPhoto(p)}
                        className="w-10 h-10 rounded-xl border border-white/10 hover:border-[#67E8F9] hover:shadow-[0_0_16px_-4px_rgba(103,232,249,0.7)] transition-all flex-shrink-0 relative group/thumb overflow-hidden"
                        title="Preview full size"
                        aria-label={`Preview ${p.caption || "photo"}`}
                        data-testid={`creator-photo-preview-${p.id}`}
                      >
                        <img src={imgUrl(p.image_path)} alt="" className="w-full h-full object-cover" />
                        <span className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/40 flex items-center justify-center transition-colors">
                          <ImageIcon className="w-3.5 h-3.5 text-white opacity-0 group-hover/thumb:opacity-100 transition-opacity" />
                        </span>
                      </button>
                      <div className="font-mono text-xs text-white/75 truncate">{p.caption || "(no caption)"}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-[10px] text-white/30 mr-2 hidden sm:inline">{new Date(p.created_at || Date.now()).toLocaleDateString()}</span>
                      <button onClick={() => startEditPhoto(p)} className="p-2 rounded-full text-white/50 hover:text-[#FCA311] hover:bg-white/5 transition-colors" title="Edit" data-testid={`creator-photo-edit-btn-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deletePhoto(p)} className="p-2 rounded-full text-white/50 hover:text-[#E63946] hover:bg-white/5 transition-colors" title="Delete" data-testid={`creator-photo-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Full-size photo lightbox — clicks anywhere or Escape close it. */}
      {previewPhoto && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black/85 backdrop-blur-sm p-6 cursor-zoom-out"
          onClick={() => setPreviewPhoto(null)}
          onKeyDown={(e) => { if (e.key === "Escape") setPreviewPhoto(null); }}
          role="dialog"
          aria-modal="true"
          aria-label={`Full-size preview of ${previewPhoto.caption || "photo"}`}
          data-testid="creator-photo-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setPreviewPhoto(null); }}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white/80 hover:text-[#E63946] hover:border-[#E63946] transition-colors"
            title="Close (Esc)"
            aria-label="Close preview"
            data-testid="creator-photo-lightbox-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="max-w-[92vw] max-h-[85vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={imgUrl(previewPhoto.image_path)}
              alt={previewPhoto.caption || ""}
              className="max-w-full max-h-[80vh] object-contain border border-white/15 shadow-[0_20px_80px_rgba(0,0,0,0.7)]"
            />
            {previewPhoto.caption && (
              <div className="font-mono text-xs text-white/70 max-w-lg text-center">{previewPhoto.caption}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
